import React, { useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { EmailOutlined, MoreVert, Person, Warning } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  colleagueToUserEmailMapping,
  enrichDepartmentusers,
  getOptionLabelOfUserOrEmailMapping
} from "../../../utils/type/type.utils";
import {
  deleteCostCenter,
  getAllCostcentersForCompanyId,
  getAllUsers,
  setCostCenterUnitUser,
  updateCostCenterProperties
} from "../../../utils/axios-requests";
import { Department } from "./departments.component";
import { UserOrEmailMapping } from "../typings/admin.types";
import { StyledDepartmentCardRoot } from "../admin-styled.component";
import { handleDepartmentUpdateError } from "../functions/handleDepartmentUpdateError.function";
import AddDepartmentModal from "./add-department-modal.partial";
import { useRemoteFetchDepartmentUsers } from "../../../hooks/Remote/User/UserInfo/useRemoteFetchDepartmentUsers";
import { toValidMuiColor } from "../../../utils/theme-color.utils";
import { COMPANY_CARD_COLOR_FALLBACK } from "../CompanyCard/company-card.component";

interface Props {
  departmentName: string;
  costCenterResponsibleMail: string;
  companyId: number;
  isDepartment: boolean;
  id: string;
  active?: boolean;
  departments: Department[];
  setDepartments: React.Dispatch<React.SetStateAction<Department[]>>;
  description?: string;
  color?: string | null;
  aliases?: string[];
  userInfo?: {
    fullName: string;
    photo?: string | null;
  };
  fetchAllCostCenterForCompany: () => Promise<void>;
}

export default function DepartmentCard({
  departmentName,
  costCenterResponsibleMail,
  companyId,
  isDepartment,
  id,
  active,
  description,
  color,
  aliases,
  userInfo,
  departments,
  setDepartments,
  fetchAllCostCenterForCompany
}: Readonly<Props>) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [editDepartment, setEditDepartment] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [users, setUsers] = useState<UserOrEmailMapping[]>([]);
  const [isManageDialogOpen, setIsManageDialogOpen] = useState(false);
  const [sureToDelete, setSureToDelete] = useState(false);
  const [departmentUsers, setDepartmentUsers] = useState<UserOrEmailMapping[]>([]);

  const { mutateAsync: fetchDepartmentUsers } = useRemoteFetchDepartmentUsers();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const inactiveWarningTooltipDesc =
    "This department is deactivated and can not be used by the users";

  const accentColor = toValidMuiColor(color, COMPANY_CARD_COLOR_FALLBACK);
  const hasLeader = Boolean(userInfo?.fullName?.trim() || costCenterResponsibleMail?.trim());
  const leaderName = userInfo?.fullName?.trim()
    ? userInfo.fullName.trim()
    : costCenterResponsibleMail?.trim() || t("_departmentCard_noLeaderAssigned");

  const fetchCostCenterUsers = () => {
    Promise.all([getAllUsers(), fetchDepartmentUsers(id)]).then(([usersRes, depUsersRes]) => {
      setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      setDepartmentUsers(
        enrichDepartmentusers(colleagueToUserEmailMapping(depUsersRes), usersRes.data)
      );
    });
  };

  return (
    <StyledDepartmentCardRoot data-testid="department-styled-card-parent">
      <Box
        data-testid="department-card-color-band"
        sx={{
          height: 24,
          width: "100%",
          bgcolor: accentColor
        }}
      />

      <Box sx={{ px: "18px", pt: "12px", pb: "18px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            mb: "3px"
          }}
        >
          <Typography
            data-testid={`department-card-${id}`}
            component="h2"
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: 17,
              fontWeight: 600,
              color: "text.primary",
              overflowWrap: "anywhere"
            }}
          >
            {departmentName}
          </Typography>

          <IconButton
            onClick={handleClick}
            data-testid="edit-department-btn"
            aria-label={t("Edit")}
            sx={{
              flexShrink: 0,
              width: 28,
              height: 28,
              borderRadius: "6px",
              bgcolor: "primary.main",
              color: "primary.contrastText",
              "&:hover": {
                bgcolor: "primary.dark"
              }
            }}
          >
            <MoreVert sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        <Typography
          component="p"
          sx={{
            fontSize: 14,
            fontWeight: 500,
            color: "text.secondary",
            mb: "14px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}
        >
          {description}
        </Typography>

        <Box sx={{ height: "0.5px", bgcolor: "divider", mb: "12px" }} />

        <Box
          data-testid="department-styled-card-middle"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            mb: "14px"
          }}
        >
          <Tooltip
            disableHoverListener={!costCenterResponsibleMail}
            title={<Typography variant="body2">{costCenterResponsibleMail}</Typography>}
          >
            <Box
              component="span"
              sx={{
                position: "relative",
                display: "inline-flex",
                flexShrink: 0
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  bgcolor: "action.hover",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                {userInfo?.photo ? (
                  <Box
                    component="img"
                    src={userInfo.photo}
                    alt={leaderName}
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <Person sx={{ fontSize: 16, color: "text.secondary" }} />
                )}
              </Box>
              {costCenterResponsibleMail ? (
                <Box
                  aria-hidden
                  sx={{
                    position: "absolute",
                    bottom: -2,
                    right: -2,
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "background.paper",
                    color: "text.secondary",
                    border: theme => `0.5px solid ${theme.palette.divider}`,
                    boxShadow: theme =>
                      theme.palette.mode === "dark"
                        ? "0 1px 2px rgba(0,0,0,0.35)"
                        : "0 1px 2px rgba(0,0,0,0.08)"
                  }}
                >
                  <EmailOutlined sx={{ fontSize: "0.625rem", color: "inherit" }} />
                </Box>
              ) : null}
            </Box>
          </Tooltip>

          <Typography
            component="span"
            sx={{
              fontSize: 15,
              fontWeight: 500,
              color: hasLeader ? "text.primary" : "text.secondary",
              fontStyle: hasLeader ? "normal" : "italic",
              overflowWrap: "anywhere",
              minWidth: 0
            }}
          >
            {leaderName}
          </Typography>

          {hasLeader ? (
            <Typography
              component="span"
              sx={{
                fontSize: 11,
                color: "text.secondary",
                marginLeft: "auto",
                flexShrink: 0
              }}
            >
              {t("Lead")}
            </Typography>
          ) : null}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
          }}
        >
          {!active ? (
            <Tooltip
              title={<Typography>{t(inactiveWarningTooltipDesc)}</Typography>}
              aria-label={inactiveWarningTooltipDesc}
            >
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  fontSize: 11,
                  fontWeight: 500,
                  color: "#92400E",
                  bgcolor: "#FEF3C7",
                  padding: "3px 8px",
                  borderRadius: "99px"
                }}
              >
                <Warning sx={{ fontSize: 14, flexShrink: 0 }} />
                <Box component="span" sx={{ pt: "2px", lineHeight: 1 }}>
                  {t("Deactivated")}
                </Box>
              </Box>
            </Tooltip>
          ) : null}

          <Button
            data-testid="manage-depUsers-button"
            onClick={() => {
              setIsManageDialogOpen(true);
              fetchCostCenterUsers();
            }}
            sx={{ marginLeft: "auto" }}
          >
            {t("Manage")}
          </Button>
        </Box>
      </Box>

      {/* menu item for more options of department */}
      <Menu
        data-testid="edit-department-card-menu"
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem
          data-testid="edit-department-card-menu-edit-item"
          onClick={() => {
            handleClose();
            setEditDepartment(true);
          }}
        >
          {t("Edit")}
        </MenuItem>
        <MenuItem
          data-testid="edit-department-card-menu-duplicate-item"
          onClick={() => {
            handleClose();
            updateCostCenterProperties(companyId, {
              id: "",
              companyId: companyId,
              name: departmentName + " - " + t("Copy"),
              costCenterResponsibleMail: costCenterResponsibleMail,
              description: description,
              active: false
            })
              .then(() =>
                getAllCostcentersForCompanyId(companyId).then(r => setDepartments(r.data))
              )
              .catch(err => handleDepartmentUpdateError(err, enqueueSnackbar, t));
          }}
        >
          {t("Duplicate")}
        </MenuItem>
        <MenuItem
          data-testid="edit-department-card-menu-deactivate-item"
          onClick={() => {
            handleClose();
            updateCostCenterProperties(companyId, {
              id: id,
              companyId: companyId,
              name: departmentName,
              costCenterResponsibleMail: costCenterResponsibleMail,
              description: description,
              active: !active
            }).then(() =>
              getAllCostcentersForCompanyId(companyId).then(r => setDepartments(r.data))
            );
          }}
        >
          {active ? t("Deactivate") : t("Activate")}
        </MenuItem>
        {!active && (
          <MenuItem
            data-testid="edit-department-card-menu-delete-item"
            onClick={() => {
              handleClose();
              setSureToDelete(true);
            }}
          >
            {t("Delete")}
          </MenuItem>
        )}
      </Menu>

      {/* add department modal */}
      <AddDepartmentModal
        open={editDepartment}
        setOpen={setEditDepartment}
        active={active || false}
        companyId={companyId}
        isDepartment={isDepartment}
        departmentName={departmentName}
        departments={departments}
        setDepartments={setDepartments}
        id={id}
        description={description}
        userInfo={userInfo}
        costCenterResponsibleMail={costCenterResponsibleMail}
        fetchAllCostCenterForCompany={fetchAllCostCenterForCompany}
        dataTestId={"edit-department-modal"}
        color={color}
        aliases={aliases}
      />

      {/* editing department users dialog */}
      <Dialog
        onClose={() => setIsManageDialogOpen(false)}
        aria-labelledby="simple-dialog-title"
        open={isManageDialogOpen}
        fullWidth
      >
        <DialogContent
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            flexDirection: "column",
            padding: "20px"
          }}
        >
          <Typography sx={{ my: 1 }}>{t("Department user")}</Typography>
          <Autocomplete
            multiple
            disableCloseOnSelect
            id="tags-outlined9"
            data-testid={"autocomplete"}
            options={users}
            limitTags={20}
            filterSelectedOptions
            filterOptions={(options, { inputValue }) =>
              options.filter(option => {
                const search = inputValue.toLowerCase();
                return (
                  option.email?.toLowerCase().includes(search) ||
                  option.firstName?.toLowerCase().includes(search) ||
                  option.surname?.toLowerCase().includes(search) ||
                  getOptionLabelOfUserOrEmailMapping(option).toLowerCase().includes(search)
                );
              })
            }
            isOptionEqualToValue={(option: UserOrEmailMapping, value: UserOrEmailMapping) =>
              option.email === value.email
            }
            getOptionLabel={getOptionLabelOfUserOrEmailMapping}
            style={{ width: "100%", marginBottom: 20 }}
            onChange={(event, values) => {
              event.persist();
              setDepartmentUsers(enrichDepartmentusers(values, users));
            }}
            value={departmentUsers}
            renderInput={params => (
              <TextField
                {...params}
                variant="outlined"
                fullWidth
                placeholder={t("Choose your colleagues")}
              />
            )}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => {
                const { key, ...rest } = getTagProps({ index });
                return (
                  <Chip
                    {...getTagProps({ index })}
                    {...rest}
                    label={getOptionLabelOfUserOrEmailMapping(option, true)}
                    key={key}
                  />
                );
              })
            }
          />
          <Button
            data-testid="confirm-depUsers-button"
            onClick={() => {
              setCostCenterUnitUser({
                costCenterUnitId: id,
                emails: departmentUsers.map(u => u.email) as string[]
              }).then(() => {
                setIsManageDialogOpen(false);
                enqueueSnackbar(t("Successfully changed department user"), { variant: "success" });
              });
            }}
          >
            {t("Confirm")}
          </Button>
        </DialogContent>
      </Dialog>

      {/* deleting confirmation department dialog */}
      <Dialog
        open={sureToDelete}
        onClose={() => setSureToDelete(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <Typography variant={"h5"} sx={{ marginBottom: "10px" }}>
            {t("Are you sure you want to delete this department?")}
          </Typography>
          <DialogContentText id="alert-dialog-description">
            {t(
              "By deleting this department all users inside the department will be removed from it"
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSureToDelete(false)} color="secondary" data-testid="cancel-btn">
            {t("Cancel")}
          </Button>
          <Button
            data-testid="confirm-deletion-btn"
            onClick={() => {
              deleteCostCenter(id, companyId).then(() => {
                getAllCostcentersForCompanyId(companyId).then(r => {
                  setDepartments(r.data);
                  setSureToDelete(false);
                });
              });
            }}
            color="primary"
            autoFocus
          >
            {t("Delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </StyledDepartmentCardRoot>
  );
}
